import { useState, useEffect, useRef } from 'react'
import { useDisconnectionAlertDialog } from '@/stores/useDisconnectionAlertDialog'
import { useDisconnectionAlertSetting } from '@/hooks/useDisconnectionAlertSetting'
import { useAuth } from '@/hooks/useAuth'
import { disconnectionAlertService } from '@/services/disconnection_alert_service'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertScopeType,
  DisconnectionAlertSchedule,
  DisconnectionAlertRecipient,
  DisconnectionAlertActiveWindow,
} from '@magnetar/admin-api-interfaces'
import { Trash2, Plus, Clock, Mail, Settings, History } from 'lucide-react'
import { useDisconnectionAlertLogs } from '@/hooks/useDisconnectionAlertLogs'
import { format } from 'date-fns'

// 時間選項（15 分鐘倍數）
const TIME_OPTIONS: string[] = []
for (let h = 0; h < 24; h++) {
  for (const m of [0, 15, 30, 45]) {
    TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
  }
}

// Scope 標籤
const SCOPE_LABELS: Record<AlertScopeType, string> = {
  [AlertScopeType.system]: '系統',
  [AlertScopeType.ad_supplier]: '供應商',
  [AlertScopeType.provider]: 'Provider',
}

export function DisconnectionAlertDialog() {
  const { isOpen, scopeType, scopeId, closeDialog } = useDisconnectionAlertDialog()
  const { token } = useAuth()
  const { setting, isLoading, mutate } = useDisconnectionAlertSetting({
    scopeType,
    scopeId,
    enabled: isOpen,
  })

  const [activeTab, setActiveTab] = useState('settings')
  const [isSaving, setIsSaving] = useState(false)

  // 設定表單狀態
  const [isEnabled, setIsEnabled] = useState(setting?.is_enabled ?? true)
  const [offlineThreshold, setOfflineThreshold] = useState(
    setting?.offline_threshold_minutes ?? 60,
  )
  const [thresholdInput, setThresholdInput] = useState(
    String(setting?.offline_threshold_minutes ?? 60),
  )
  const [emailSubject, setEmailSubject] = useState(setting?.email_subject ?? '')
  const isDirty = useRef(false)

  // 新增排程表單
  const [newCheckTime, setNewCheckTime] = useState('09:00')
  const [isAddingSchedule, setIsAddingSchedule] = useState(false)

  // 新增收件人表單
  const [newRecipientName, setNewRecipientName] = useState('')
  const [newRecipientEmail, setNewRecipientEmail] = useState('')
  const [isAddingRecipient, setIsAddingRecipient] = useState(false)

  // 新增工作時段表單
  const [newWindowStart, setNewWindowStart] = useState('09:00')
  const [newWindowEnd, setNewWindowEnd] = useState('18:00')
  const [isAddingWindow, setIsAddingWindow] = useState(false)

  // 紀錄查詢
  const { logs, isLoading: isLoadingLogs } = useDisconnectionAlertLogs({
    alertSettingId: setting?.alert_setting_id,
    limit: 20,
    enabled: isOpen && activeTab === 'logs' && !!setting?.alert_setting_id,
  })

  // 同步設定到表單狀態（僅在非用戶修改時）
  useEffect(() => {
    if (setting && !isDirty.current) {
      setIsEnabled(setting.is_enabled)
      setOfflineThreshold(setting.offline_threshold_minutes)
      setThresholdInput(String(setting.offline_threshold_minutes))
      setEmailSubject(setting.email_subject ?? '')
    }
  }, [setting])

  // 儲存設定
  const handleSaveSetting = async () => {
    setIsSaving(true)
    try {
      await disconnectionAlertService.upsertSetting(
        {
          scope_type: scopeType,
          scope_id: scopeId ?? undefined,
          is_enabled: isEnabled,
          offline_threshold_minutes: offlineThreshold,
          email_subject: emailSubject || undefined,
        },
        token ?? undefined,
      )
      isDirty.current = false
      await mutate()
    } finally {
      setIsSaving(false)
    }
  }

  // 新增排程
  const handleAddSchedule = async () => {
    if (!setting?.alert_setting_id) return
    setIsAddingSchedule(true)
    try {
      const result = await disconnectionAlertService.createSchedule(
        {
          alert_setting_id: setting.alert_setting_id,
          check_time: newCheckTime,
        },
        token ?? undefined,
      )
      if (result.success) {
        await mutate()
        setNewCheckTime('09:00')
      }
    } finally {
      setIsAddingSchedule(false)
    }
  }

  // 刪除排程
  const handleDeleteSchedule = async (scheduleId: number) => {
    await disconnectionAlertService.deleteSchedule(scheduleId, token ?? undefined)
    await mutate()
  }

  // 切換排程啟用狀態
  const handleToggleSchedule = async (schedule: DisconnectionAlertSchedule) => {
    await disconnectionAlertService.updateSchedule(
      schedule.schedule_id,
      { is_enabled: !schedule.is_enabled },
      token ?? undefined,
    )
    await mutate()
  }

  // 新增收件人
  const handleAddRecipient = async () => {
    if (!setting?.alert_setting_id || !newRecipientName || !newRecipientEmail) return
    setIsAddingRecipient(true)
    try {
      const result = await disconnectionAlertService.createRecipient(
        {
          alert_setting_id: setting.alert_setting_id,
          name: newRecipientName,
          email: newRecipientEmail,
        },
        token ?? undefined,
      )
      if (result.success) {
        await mutate()
        setNewRecipientName('')
        setNewRecipientEmail('')
      }
    } finally {
      setIsAddingRecipient(false)
    }
  }

  // 刪除收件人
  const handleDeleteRecipient = async (recipientId: number) => {
    await disconnectionAlertService.deleteRecipient(recipientId, token ?? undefined)
    await mutate()
  }

  // 切換收件人啟用狀態
  const handleToggleRecipient = async (recipient: DisconnectionAlertRecipient) => {
    await disconnectionAlertService.updateRecipient(
      recipient.recipient_id,
      { is_enabled: !recipient.is_enabled },
      token ?? undefined,
    )
    await mutate()
  }

  // 新增工作時段
  const handleAddActiveWindow = async () => {
    if (!setting?.alert_setting_id) return
    setIsAddingWindow(true)
    try {
      const result = await disconnectionAlertService.createActiveWindow(
        {
          alert_setting_id: setting.alert_setting_id,
          start_time: newWindowStart,
          end_time: newWindowEnd,
        },
        token ?? undefined,
      )
      if (result.success) {
        await mutate()
        setNewWindowStart('09:00')
        setNewWindowEnd('18:00')
      }
    } finally {
      setIsAddingWindow(false)
    }
  }

  // 刪除工作時段
  const handleDeleteActiveWindow = async (windowId: number) => {
    await disconnectionAlertService.deleteActiveWindow(windowId, token ?? undefined)
    await mutate()
  }

  // 切換工作時段啟用狀態
  const handleToggleActiveWindow = async (window: DisconnectionAlertActiveWindow) => {
    await disconnectionAlertService.updateActiveWindow(
      window.window_id,
      { is_enabled: !window.is_enabled },
      token ?? undefined,
    )
    await mutate()
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeDialog}>
      <DialogContent className="w-[700px] max-w-[90vw] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>斷線警示設定</DialogTitle>
          <DialogDescription>
            設定 {SCOPE_LABELS[scopeType]}
            {scopeId ? ` (ID: ${scopeId})` : ''} 的設備斷線警示通知
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="settings" className="flex items-center gap-1">
                <Settings className="h-4 w-4" />
                基本設定
              </TabsTrigger>
              <TabsTrigger value="schedules" className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                檢查時間
              </TabsTrigger>
              <TabsTrigger value="recipients" className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                收件人
              </TabsTrigger>
              <TabsTrigger value="logs" className="flex items-center gap-1">
                <History className="h-4 w-4" />
                發送紀錄
              </TabsTrigger>
            </TabsList>

            {/* 基本設定 */}
            <TabsContent value="settings" className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is-enabled">啟用警示</Label>
                  <p className="text-sm text-muted-foreground">開啟後將在設定的時間點檢查設備狀態</p>
                </div>
                <Switch id="is-enabled" checked={isEnabled} onCheckedChange={(v) => { isDirty.current = true; setIsEnabled(v) }} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="threshold">離線判定閾值（分鐘）</Label>
                <Input
                  id="threshold"
                  type="number"
                  min={1}
                  value={thresholdInput}
                  onChange={(e) => {
                    isDirty.current = true
                    setThresholdInput(e.target.value)
                    const parsed = parseInt(e.target.value)
                    if (!isNaN(parsed) && parsed > 0) {
                      setOfflineThreshold(parsed)
                    }
                  }}
                  onBlur={() => {
                    const parsed = parseInt(thresholdInput)
                    if (isNaN(parsed) || parsed < 1) {
                      setOfflineThreshold(60)
                      setThresholdInput('60')
                    } else {
                      setOfflineThreshold(parsed)
                      setThresholdInput(String(parsed))
                    }
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  設備超過此時間未連線將被判定為離線
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-subject">郵件主旨（選填）</Label>
                <Input
                  id="email-subject"
                  placeholder="【警示】診所設備斷線通知"
                  value={emailSubject}
                  onChange={(e) => { isDirty.current = true; setEmailSubject(e.target.value) }}
                />
              </div>

              <Button onClick={handleSaveSetting} disabled={isSaving}>
                {isSaving ? '儲存中...' : '儲存設定'}
              </Button>
            </TabsContent>

            {/* 檢查時間排程 */}
            <TabsContent value="schedules" className="mt-4 space-y-4 overflow-y-auto max-h-[calc(85vh-200px)]">
              {!setting?.alert_setting_id ? (
                <p className="text-muted-foreground text-center py-8">請先儲存基本設定</p>
              ) : (
                <>
                  {/* 工作時段區塊 */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-base font-semibold">工作時段</Label>
                      <p className="text-sm text-muted-foreground">
                        設定檢查生效的時段，僅在工作時段內的檢查時間才會觸發警示
                      </p>
                    </div>

                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">開始時間</Label>
                        <select
                          className="w-full mt-1 px-3 py-2 border rounded-md bg-background text-sm"
                          value={newWindowStart}
                          onChange={(e) => setNewWindowStart(e.target.value)}
                        >
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                      <span className="pb-2 text-muted-foreground">~</span>
                      <div className="flex-1">
                        <Label className="text-xs">結束時間</Label>
                        <select
                          className="w-full mt-1 px-3 py-2 border rounded-md bg-background text-sm"
                          value={newWindowEnd}
                          onChange={(e) => setNewWindowEnd(e.target.value)}
                        >
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                      <Button onClick={handleAddActiveWindow} disabled={isAddingWindow} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        新增
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {(!setting.active_windows || setting.active_windows.length === 0) && (
                        <p className="text-muted-foreground text-center py-2 text-sm bg-muted/50 rounded-md">
                          未設定工作時段，所有檢查時間皆會觸發
                        </p>
                      )}
                      {setting.active_windows?.map((window) => (
                        <div
                          key={window.window_id}
                          className="flex items-center justify-between p-2 border rounded-md"
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono">{window.start_time}</span>
                            <span className="text-muted-foreground">~</span>
                            <span className="font-mono">{window.end_time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={window.is_enabled}
                              onCheckedChange={() => handleToggleActiveWindow(window)}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteActiveWindow(window.window_id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <hr className="border-border" />

                  {/* 檢查時間區塊 */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">檢查時間</Label>

                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">新增檢查時間</Label>
                        <select
                          className="w-full mt-1 px-3 py-2 border rounded-md bg-background text-sm"
                          value={newCheckTime}
                          onChange={(e) => setNewCheckTime(e.target.value)}
                        >
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                      <Button onClick={handleAddSchedule} disabled={isAddingSchedule} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        新增
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {setting.schedules?.length === 0 && (
                        <p className="text-muted-foreground text-center py-4">尚無檢查時間</p>
                      )}
                      {setting.schedules?.map((schedule) => (
                        <div
                          key={schedule.schedule_id}
                          className="flex items-center justify-between p-2 border rounded-md"
                        >
                          <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono text-lg">{schedule.check_time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={schedule.is_enabled}
                              onCheckedChange={() => handleToggleSchedule(schedule)}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* 收件人管理 */}
            <TabsContent value="recipients" className="mt-4 space-y-4">
              {!setting?.alert_setting_id ? (
                <p className="text-muted-foreground text-center py-8">請先儲存基本設定</p>
              ) : (
                <>
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                    <div>
                      <Label>姓名</Label>
                      <Input
                        placeholder="王小明"
                        value={newRecipientName}
                        onChange={(e) => setNewRecipientName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="example@mail.com"
                        value={newRecipientEmail}
                        onChange={(e) => setNewRecipientEmail(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={handleAddRecipient}
                      disabled={isAddingRecipient || !newRecipientName || !newRecipientEmail}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      新增
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {setting.recipients?.length === 0 && (
                      <p className="text-muted-foreground text-center py-4">尚無收件人</p>
                    )}
                    {setting.recipients?.map((recipient) => (
                      <div
                        key={recipient.recipient_id}
                        className="flex items-center justify-between p-3 border rounded-md"
                      >
                        <div>
                          <div className="font-medium">{recipient.name}</div>
                          <div className="text-sm text-muted-foreground">{recipient.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={recipient.is_enabled}
                            onCheckedChange={() => handleToggleRecipient(recipient)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRecipient(recipient.recipient_id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* 發送紀錄 */}
            <TabsContent value="logs" className="mt-4">
              {isLoadingLogs ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : logs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">尚無發送紀錄</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {logs.map((log) => (
                    <div key={log.log_id} className="p-3 border rounded-md">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-mono">{log.check_time}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            {format(new Date(log.checked_at), 'yyyy-MM-dd HH:mm:ss')}
                          </span>
                        </div>
                        <div
                          className={`px-2 py-0.5 rounded text-xs ${
                            log.offline_clinic_count > 0
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {log.offline_clinic_count > 0
                            ? `${log.offline_clinic_count} 間離線`
                            : '全部在線'}
                        </div>
                      </div>
                      {log.offline_clinic_count > 0 && (
                        <div className="text-sm text-muted-foreground">
                          離線診所：
                          {log.offline_clinics.map((c) => c.clinic_name).join('、')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={closeDialog}>
            關閉
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DisconnectionAlertDialog
